import React, {useCallback} from 'react'
import {Lang} from 'yougile-lang'
import {ContentTypeUtil} from 'yougile-util'
import {ChatMessage, DataObjectId, GroupChat} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {MessageBox} from '@components/message-box'
import {getHub} from '@components/message-forwarding/utils/get-hub'
import {useChatStore} from '@components/right-panel/chat/chat-store'
import {useRightPanelContentStore} from '@components/right-panel/right-panel-content/right-panel-content-store'
import {ChatUtils} from '@extensions/chat-utils'
import {MessageUtils} from '@extensions/message-utils'

export const useForwardedMessageClick = (
  message: ChatMessage
): ((e: React.MouseEvent) => void) => {
  const [state] = useChatStore()
  const [, rightPanelStore] = useRightPanelContentStore()

  return useCallback(
    (e: React.MouseEvent): void => {
      const target = e.target as HTMLElement

      const process = (selectedHref: string): void => {
        const userDataFilePath = '/user-data/'
        if (!selectedHref.startsWith(userDataFilePath)) {
          return
        }
        const links = MessageUtils.getMessagesLinks(state.messagesList)
        rightPanelStore.setSelectedFilesToView(
          MessageUtils.getFileViewData(links),
          MessageUtils.getLinkIndex(links, selectedHref)
        )
        e.preventDefault()
      }

      if (target?.nodeName === 'IMG') {
        const parentNode = target.parentNode as HTMLAnchorElement
        const selectedHref = parentNode.pathname
        process(selectedHref)
        return
      }

      if (target?.nodeName === 'A') {
        const node = target as HTMLAnchorElement
        const selectedHref = node.pathname
        const extension = selectedHref.split('.')?.pop()?.split('?')[0]
        if (extension && ContentTypeUtil.isVideoExtension(extension)) {
          process(selectedHref)
          return
        }
      }

      const originalChatId = message.properties.originalChatId as DataObjectId
      const {box, hub} = getHub(originalChatId)

      if (!box || !hub) {
        return
      }
      const isGroupChat = GroupChat.isGroupChat(hub)
      if (isGroupChat) {
        const hierarchy = DepartmentsClient.getHierarchy(box)
        const checkShow = PermissionsChecker.checkShow(hierarchy, hub, {})
        if (!checkShow) {
          MessageBox.warn(Lang.msg.messageForwarding.viewWarning)
          return
        }
      }
      ChatUtils.openChat(hub, {
        messageTime: message.properties.originalMessageTime as number
      })
    },
    [message]
  )
}
